import React, { useEffect, useState, CSSProperties, forwardRef, ChangeEvent } from 'react';
import { Redirect } from 'react-router-dom';
import useCurrentUser from 'Hooks/useCurrentUser';
import dayjs from 'dayjs';
import useScreen from 'Hooks/useScreen';
import { formatNumberToDisplayType } from 'modules/Utils';
import CardSymphony from 'elements/CardSymphony';
import TableElement, { ColDef } from 'elements/TableElement';
import MenuItem from '@mui/material/MenuItem';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ButtonSymphony from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';
import Loader from 'elements/Loader';
import { styx, clsx } from 'modules/Utils';
import InputSymphony from 'elements/InputSymphony';
import { getTopPerformingBrands_api } from 'api/adminApi';
import DatePickerSymphony from 'elements/DatePickerSymphony';
import DropdownArrow from 'components/shareable/DropdownArrow';
import Colors from 'modules/Colors';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

interface TopPerformingProps {}

type DateRangeOption = '7d' | '14d' | '30d' | 'custom';
type EventTypeOption = 'Page View' | 'StreamingLinkClicked' | 'Spotify Pre-save Success' | 'Apple Pre-Save Success';

interface UserData {
  id: number;
  name: string | null;
  lastName: string | null;
  email: string;
  fullName: string;
}

interface BrandData {
  id: number;
  name: string;
  slug: string;
  createdAt: string;
  pageViews?: number;
  eventCount?: number;
  eventType?: string;
  users: UserData[];
  tier: string;
  imageUrl?: string;
  spotify_monthly_listeners?: number;
  instagram_followers?: number;
  tiktok_followers?: number;
  youtube_subscribers?: number;
  lastLoggedIn?: string;
  lastLoginTimestamp?: number;
  presaveStats?: {
    totalPresaves?: number;
    foreverSaves?: number;
    presaveRate?: number;
  };
  campaignStats?: {
    activeCampaigns?: number;
    totalCampaigns?: number;
  };
}

interface PaginationData {
  limit: number;
  offset: number;
  totalPages: number;
}

interface DateRangeData {
  since: string;
  until: string;
  timeRange: DateRangeOption;
}

interface ApiResponse {
  brands: BrandData[];
  totalCount: number;
  pagination: PaginationData;
  dateRange: DateRangeData;
}

interface ColumnOption {
  id: string;
  title: string;
  isFixed?: boolean;
}

const PAGE_SIZE = 25;

/**
 * Custom date selector input component
 */
const CustomDateSelector = forwardRef(({
  value,
  onClick
}: any, ref: any) => {
  return (
    <button
      className="customDateSelectorButton"
      onClick={onClick}
      ref={ref}
      style={sty.customDateSelectorButton}
    >
      {value}
    </button>
  );
});

/**
 * Admin component that displays brands with the highest pageviews
 */
const TopPerforming: React.FC<TopPerformingProps> = () => {
  const { isSymphonyAdmin: isAdmin } = useCurrentUser();
  const { mStyle, mobileView } = useScreen();

  // State for data and loading
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [brands, setBrands] = useState<BrandData[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  
  // State for pagination
  const [limit, setLimit] = useState<number>(PAGE_SIZE);
  const [offset, setOffset] = useState<number>(0);
  
  // State for date range
  const [timeRange, setTimeRange] = useState<DateRangeOption>('7d');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(dayjs().subtract(7, 'day').toDate());
  const [customEndDate, setCustomEndDate] = useState<Date | null>(dayjs().toDate());
  
  // State for event type
  const [eventType, setEventType] = useState<EventTypeOption>('Page View');

  // State for column visibility dropdown
  const [isShowColumnOptions, setIsShowColumnOptions] = useState<boolean>(false);

  // Define available columns
  const columnOptions: ColumnOption[] = [
    { id: 'name', title: 'Brand Name', isFixed: true },
    { id: 'createdAt', title: 'Joined At' },
    { id: 'pageViews', title: 'Total Page Views' },
    { id: 'spotify_monthly_listeners', title: 'Spotify Listeners' },
    { id: 'instagram_followers', title: 'Instagram' },
    { id: 'tiktok_followers', title: 'TikTok' },
    { id: 'youtube_subscribers', title: 'YouTube' },
    { id: 'activeCampaigns', title: 'Active Campaigns' },
    { id: 'totalCampaigns', title: 'Total Campaigns' },
    { id: 'presaveRate', title: 'Presave Rate' },
    { id: 'foreverSaves', title: 'Forever Saves' },
    { id: 'users', title: 'User Emails' },
    { id: 'contact', title: 'Contact' },
  ];

  // State for visible columns - initially all visible
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    columnOptions.map(col => col.id)
  );

  /**
   * Updates the visible columns state when a column is toggled
   * @param columnId - The ID of the column being toggled
   * @param checked - Whether the column should be visible or not
   */
  const handleColumnToggle = (columnId: string, checked: boolean) => {
    let newVisibleColumns: string[];
    
    if (checked) {
      // Add column to visible columns
      newVisibleColumns = [...visibleColumns, columnId];
    } else {
      // Remove column from visible columns
      newVisibleColumns = visibleColumns.filter(id => id !== columnId);
    }
    
    // Always keep "name" column visible
    if (!newVisibleColumns.includes('name')) {
      newVisibleColumns.push('name');
    }
    
    // Ensure at least 2 columns are always visible
    if (newVisibleColumns.length >= 2) {
      setVisibleColumns(newVisibleColumns);
    }
  };

  /**
   * Fetches top performing brands data from API
   * @param isRefresh - Indicates if this is a manual refresh request
   */
  const fetchData = async (isRefresh: boolean = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const params: any = {
        limit,
        offset,
        timeRange,
        type: eventType,
      };

      // Add custom date range parameters if needed
      if (timeRange === 'custom' && customStartDate && customEndDate) {
        params.startDate = dayjs(customStartDate).format('YYYY-MM-DD');
        params.endDate = dayjs(customEndDate).format('YYYY-MM-DD');
      }

      console.log('Fetching data with params:', params);
      const response = await getTopPerformingBrands_api(params);
      console.log('API response:', response);
      
      setBrands(response.brands);
      setTotalCount(response.totalCount);
    } catch (error) {
      console.error('Error fetching top performing brands:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /**
   * Handles manual data refresh
   */
  const handleRefresh = () => {
    fetchData(true);
  };

  // Initial data fetch
  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, timeRange, limit, offset, eventType]);

  // Handle date range change
  const handleTimeRangeChange = (value: string) => {
    console.log('Time range changed to:', value);
    const newTimeRange = value as DateRangeOption;
    setTimeRange(newTimeRange);
    setOffset(0); // Reset pagination when changing date range
  };

  // Handle event type change
  const handleEventTypeChange = (value: string) => {
    console.log('Event type changed to:', value);
    const newEventType = value as EventTypeOption;
    setEventType(newEventType);
    setOffset(0); // Reset pagination when changing event type
  };

  // Handle custom start date change
  const handleStartDateChange = (date: Date | null) => {
    setCustomStartDate(date);
  };

  // Handle custom end date change
  const handleEndDateChange = (date: Date | null) => {
    setCustomEndDate(date);
  };

  // Handle applying custom date range
  const handleApplyCustomDates = () => {
    if (customStartDate && customEndDate) {
      fetchData();
    }
  };

  // Handle pagination
  const handleLoadMore = async () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
  };

  /**
   * Generates a mailto link with all user emails
   */
  const getMailtoLink = (users: UserData[]): string => {
    const emails = users.map(user => user.email).join(',');
    return `mailto:${emails}`;
  };

  /**
   * Formats date for display
   */
  const formatDate = (dateString: string): string => {
    return dayjs(dateString).format('MMM D, YYYY');
  };

  /**
   * Renders email list for multiple users
   */
  const renderUserEmails = (users: UserData[]): JSX.Element => {
    return (
      <div className="userEmailsContainer" style={sty.userEmailsContainer}>
        {users.map((user) => (
          <div key={user.id} className="userEmail" style={sty.userEmail}>
            <span>{user.email}</span>
            {user.fullName !== 'Unknown' && <span style={sty.userName}>({user.fullName})</span>}
          </div>
        ))}
      </div>
    );
  };

  /**
   * Renders custom date picker component
   */
  const renderCustomDatePicker = () => {
    if (timeRange !== 'custom') return null;
    
    return (
      <div className="customDatePickerContainer" style={styx(
        sty.customDatePickerContainer,
        mobileView && sty.customDatePickerContainer_mobile
      )}>
        <div className="datePickerRow" style={sty.datePickerRow}>
          <span>From:</span>
          <DatePicker
            selected={customStartDate}
            onChange={handleStartDateChange}
            maxDate={customEndDate || dayjs().toDate()}
            dateFormat="MMM d, yyyy"
            customInput={<CustomDateSelector />}
            wrapperClassName="datePicker"
          />
        </div>
        <div className="datePickerRow" style={sty.datePickerRow}>
          <span>To:</span>
          <DatePicker
            selected={customEndDate}
            onChange={handleEndDateChange}
            minDate={customStartDate}
            maxDate={dayjs().toDate()}
            dateFormat="MMM d, yyyy"
            customInput={<CustomDateSelector />}
            wrapperClassName="datePicker"
          />
        </div>
        <ButtonSymphony 
          className="applyButton"
          onClick={handleApplyCustomDates}
          style={sty.applyButton}
        >
          Apply
        </ButtonSymphony>
      </div>
    );
  };

  /**
   * Formats platform metric values for display
   * @param value - The numeric value of the metric
   * @returns Formatted string representation of the metric
   */
  const formatPlatformMetric = (value?: number): string => {
    if (value === undefined || value === null) return '-';
    return String(formatNumberToDisplayType(value, 'number'));
  };

  /**
   * Formats percentage values for display
   * @param value - The percentage value (0-100 or 0-1)
   * @returns Formatted percentage string
   */
  const formatPercentage = (value?: number): string => {
    if (value === undefined || value === null) return '-';
    
    // Handle if value is already in percentage format (0-100) or decimal format (0-1)
    const percentValue = value > 1 ? value : value * 100;
    return `${percentValue.toFixed(1)}%`;
  };

  /**
   * Renders the column options dropdown for toggling column visibility
   */
  const renderColumnOptions = () => {
    if (!isShowColumnOptions) return null;
    
    return (
      <div className="columnOptionsContainer" style={sty.columnOptionsContainer}>
        <div className="columnOptionsHeader" style={sty.columnOptionsHeader}>
          <h3 style={sty.columnOptionsTitle}>Show/Hide Columns</h3>
          <p style={sty.columnOptionsSubtitle}>Select which columns to display in the table</p>
        </div>
        <div className="columnOptionsList" style={sty.columnOptionsList}>
          {columnOptions.map((column) => (
            <div key={column.id} className="columnOptionItem" style={sty.columnOptionItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={visibleColumns.includes(column.id)}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleColumnToggle(column.id, e.target.checked)}
                    disabled={column.isFixed || (visibleColumns.length <= 2 && visibleColumns.includes(column.id))}
                  />
                }
                label={column.title}
                disabled={column.isFixed || (visibleColumns.length <= 2 && visibleColumns.includes(column.id))}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Define table columns
  const allColumnDefs: ColDef[] = [
    {
      title: "Brand Name",
      field: "name",
      flex: 2,
      minWidth: 200,
      renderCell: (row: BrandData) => (
        <div className="brandNameCell" style={sty.brandNameCell}>
          <div className="brandImageContainer" style={sty.brandImageContainer}>
            {row.imageUrl ? (
              <div className="iconWrapper" style={sty.iconWrapper}>
                <Icon 
                  className="brandImage" 
                  icon={row.imageUrl} 
                  iconType="custom" 
                  size={40} 
                  isCircle={true} 
                  style={sty.brandImage}
                />
              </div>
            ) : (
              <div className="brandImagePlaceholder" style={sty.brandImagePlaceholder}>
                {row.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="brandNameWrapper" style={sty.brandNameWrapper}>
            <span>{row.name}</span>
            <span style={sty.brandSlug}>{row.slug}</span>
          </div>
          <span style={sty.tierBadge}>{row.tier.toUpperCase()}</span>
        </div>
      )
    },
    {
      title: "Joined At",
      field: "createdAt",
      flex: 1,
      minWidth: 120,
      renderCell: (row: BrandData) => (
        <div>{formatDate(row.createdAt)}</div>
      )
    },
    {
      title: "Total Page Views",
      field: "pageViews",
      flex: 1,
      minWidth: 140,
      renderCell: (row: BrandData) => (
        <div style={sty.pageViewsCell}>
          {formatNumberToDisplayType(row.eventCount || row.pageViews || 0, 'number')}
        </div>
      )
    },
    {
      title: "Spotify Listeners",
      field: "spotify_monthly_listeners",
      flex: 1,
      minWidth: 140,
      renderCell: (row: BrandData) => (
        <div className="metricCell" style={sty.metricCell}>
          {formatPlatformMetric(row.spotify_monthly_listeners)}
        </div>
      )
    },
    {
      title: "Instagram",
      field: "instagram_followers",
      flex: 1,
      minWidth: 120,
      renderCell: (row: BrandData) => (
        <div className="metricCell" style={sty.metricCell}>
          {formatPlatformMetric(row.instagram_followers)}
        </div>
      )
    },
    {
      title: "TikTok",
      field: "tiktok_followers",
      flex: 1,
      minWidth: 120,
      renderCell: (row: BrandData) => (
        <div className="metricCell" style={sty.metricCell}>
          {formatPlatformMetric(row.tiktok_followers)}
        </div>
      )
    },
    {
      title: "YouTube",
      field: "youtube_subscribers",
      flex: 1,
      minWidth: 120,
      renderCell: (row: BrandData) => (
        <div className="metricCell" style={sty.metricCell}>
          {formatPlatformMetric(row.youtube_subscribers)}
        </div>
      )
    },
    {
      title: "Active Campaigns",
      field: "activeCampaigns",
      flex: 1,
      minWidth: 140,
      renderCell: (row: BrandData) => (
        <div className="metricCell" style={sty.metricCell}>
          {row.campaignStats?.activeCampaigns !== undefined ? formatPlatformMetric(row.campaignStats.activeCampaigns) : '-'}
        </div>
      )
    },
    {
      title: "Total Campaigns",
      field: "totalCampaigns",
      flex: 1,
      minWidth: 140,
      renderCell: (row: BrandData) => (
        <div className="metricCell" style={sty.metricCell}>
          {row.campaignStats?.totalCampaigns !== undefined ? formatPlatformMetric(row.campaignStats.totalCampaigns) : '-'}
        </div>
      )
    },
    {
      title: "Presave Rate",
      field: "presaveRate",
      flex: 1,
      minWidth: 120,
      renderCell: (row: BrandData) => (
        <div className="metricCell" style={sty.metricCell}>
          {row.presaveStats?.presaveRate !== undefined ? formatPercentage(row.presaveStats.presaveRate) : '-'}
        </div>
      )
    },
    {
      title: "Forever Saves",
      field: "foreverSaves",
      flex: 1,
      minWidth: 140,
      renderCell: (row: BrandData) => (
        <div className="metricCell" style={sty.metricCell}>
          {row.presaveStats?.foreverSaves !== undefined ? formatPlatformMetric(row.presaveStats.foreverSaves) : '-'}
        </div>
      )
    },
    {
      title: "User Emails",
      field: "users",
      flex: 2,
      minWidth: 200,
      renderCell: (row: BrandData) => renderUserEmails(row.users)
    },
    {
      title: "Contact",
      field: "contact",
      flex: 1,
      minWidth: 100,
      renderCell: (row: BrandData) => (
        <a 
          href={getMailtoLink(row.users)} 
          target="_blank" 
          rel="noopener noreferrer"
          style={sty.emailLink}
        >
          <ButtonSymphony
            className="emailButton"
            variant="text"
            style={sty.emailButton}
          >
            <Icon className="emailIcon" icon="mail" style={sty.emailIcon} />
            Email
          </ButtonSymphony>
        </a>
      )
    }
  ];

  // Filter column definitions based on visible columns
  const visibleColumnDefs = allColumnDefs.filter(col => 
    col.field && visibleColumns.includes(col.field as string)
  );

  // Redirect if not admin
  if (!isAdmin) {
    return <Redirect to="/dashboard" />;
  }

  return (
    <div 
      className="topPerformingContainer"
      style={styx(
        mStyle(sty, 'topPerformingContainer')
      )}
    >
      <CardSymphony 
        className="topPerformingCard"
        style={styx(
          sty.topPerformingCard,
          mobileView && sty.topPerformingCard_mobile
        )}
      >
        <div className="topPerformingHeader" style={sty.topPerformingHeader}>
          <h1 style={sty.heading}>Top Performing Websites</h1>
          <p style={sty.subtitle}>View brands with the highest page views for your selected time period</p>
        </div>

        <div className="filterContainer" style={styx(
          sty.filterContainer,
          mobileView && sty.filterContainer_mobile
        )}>
          <div className="leftFilterSection" style={styx(
            sty.leftFilterSection,
            mobileView && sty.leftFilterSection_mobile
          )}>
            <div className="timeRangeSelector" style={sty.timeRangeSelector}>
              <span>Time Period:</span>
              <InputSymphony
                className="timeRangeSelect"
                value={timeRange}
                onOptionSelect={(option) => handleTimeRangeChange(option.value)}
                style={sty.select}
                isSelectDropdown
                options={[
                  { value: '7d', title: 'Last 7 Days' },
                  { value: '14d', title: 'Last 14 Days' },
                  { value: '30d', title: 'Last 30 Days' },
                  { value: 'custom', title: 'Custom Range' }
                ]}
              />
            </div>
            <div className="eventTypeSelector" style={sty.timeRangeSelector}>
              <span>Event Type:</span>
              <InputSymphony
                className="eventTypeSelect"
                value={eventType}
                onOptionSelect={(option) => handleEventTypeChange(option.value)}
                style={sty.select}
                isSelectDropdown
                options={[
                  { value: 'Page View', title: 'Page View' },
                  { value: 'Streaming Link Clicked', title: 'Streaming Link Clicked' },
                  { value: 'Spotify Pre-save Success', title: 'Spotify Pre-save Success' },
                  { value: 'Apple Pre-Save Success', title: 'Apple Pre-Save Success' }
                ]}
              />
            </div>
            {renderCustomDatePicker()}
          </div>
          
          <div className="rightFilterSection" style={sty.rightFilterSection}>
            <ButtonSymphony
              className="columnsButton"
              variant="outlined"
              onClick={() => setIsShowColumnOptions(!isShowColumnOptions)}
              style={sty.columnsButton}
              iconRight={
                <DropdownArrow
                  color={Colors.purple}
                  width={16}
                  height={16}
                  direction={isShowColumnOptions ? "up" : "down"}
                />
              }
            >
              Columns
            </ButtonSymphony>
            
            <ButtonSymphony
              className="refreshButton"
              onClick={handleRefresh}
              variant="outlined"
              disabled={loading || refreshing}
              style={styx(
                sty.refreshButton,
                mobileView && sty.refreshButton_mobile
              )}
            >
              <Icon className="refreshIcon" icon="reload" style={sty.refreshIcon} />
              Refresh
              {refreshing && 
                <div className="refreshLoaderContainer" style={sty.refreshLoaderContainer}>
                  <Loader size={16} />
                </div>
              }
            </ButtonSymphony>
          </div>
        </div>

        {renderColumnOptions()}

        {loading ? (
          <div className="loaderContainer" style={sty.loaderContainer}>
            <Loader size={40} title="Loading top performing brands..." />
          </div>
        ) : (
          <div className="tableContainer" style={sty.tableContainer}>
            {refreshing && 
              <div className="tableOverlayLoader" style={sty.tableOverlayLoader}>
                <Loader size={40} title="Refreshing data..." />
              </div>
            }
            <TableElement
              className="topPerformingTable"
              rowData={brands}
              columnDefs={visibleColumnDefs}
              rowsTitle="brands"
              rowsTotalOverride={totalCount}
              pagination={true}
              paginationPageSize={limit}
              paginationPageSizeSelector={[10, 25, 50]}
              onPageSizeChange={(pageSize) => setLimit(pageSize)}
              onClickRightArrowAsync={handleLoadMore}
            />
          </div>
        )}
      </CardSymphony>
    </div>
  );
};

const sty: Record<string, CSSProperties> = {
  topPerformingContainer: {
    padding: 24,
    width: '100%',
  },
  topPerformingContainer_mobile: {
    padding: 16,
  },
  topPerformingCard: {
    padding: 24,
    width: '100%',
  },
  topPerformingCard_mobile: {
    padding: 16,
  },
  topPerformingHeader: {
    marginBottom: 24,
  },
  subtitle: {
    marginTop: 8,
    color: '#666',
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    gap: 16,
  },
  filterContainer_mobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  leftFilterSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  leftFilterSection_mobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
  },
  rightFilterSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  timeRangeSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  select: {
    minWidth: 150,
  },
  customDatePickerContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  customDatePickerContainer_mobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
  },
  datePickerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  customDateSelectorButton: {
    border: '1px solid #ccc',
    borderRadius: 4,
    padding: '8px 12px',
    backgroundColor: 'white',
    minWidth: 120,
    textAlign: 'left',
    cursor: 'pointer',
  },
  applyButton: {
    marginLeft: 8,
  },
  columnsButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingLeft: 18,
    paddingRight: 14,
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    position: 'relative',
  },
  refreshButton_mobile: {
    alignSelf: 'flex-end',
    marginTop: 16,
  },
  refreshIcon: {
    fontSize: 18,
  },
  refreshLoaderContainer: {
    marginLeft: 8,
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
  },
  tableContainer: {
    position: 'relative',
    width: '100%',
    overflowX: 'auto',
  },
  tableOverlayLoader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 10,
    borderRadius: 8,
  },
  columnOptionsContainer: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 8,
    backgroundColor: '#f9f9f9',
    border: '1px solid #eee',
  },
  columnOptionsHeader: {
    marginBottom: 16,
  },
  columnOptionsTitle: {
    fontSize: 16,
    fontWeight: 600,
    margin: 0,
    marginBottom: 4,
  },
  columnOptionsSubtitle: {
    fontSize: 14,
    color: '#666',
    margin: 0,
  },
  columnOptionsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
  },
  columnOptionItem: {
    minWidth: 160,
  },
  userEmailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  userEmail: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: 14,
  },
  userName: {
    fontSize: 12,
    color: '#666',
    marginLeft: 4,
  },
  brandNameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  brandImageContainer: {
    flexShrink: 0,
    width: 40,
    height: 40,
  },
  iconWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
  },
  brandImage: {
    objectFit: 'cover',
    background: 'none',
    flexShrink: 0,
    width: '100%',
    height: '100%',
  },
  brandImagePlaceholder: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: '#e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    fontWeight: 500,
    color: '#666',
    flexShrink: 0,
  },
  brandNameWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  brandSlug: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  tierBadge: {
    fontSize: 11,
    padding: '2px 6px',
    borderRadius: 4,
    backgroundColor: '#eee',
    color: '#666',
  },
  pageViewsCell: {
    fontWeight: 500,
  },
  emailLink: {
    textDecoration: 'none',
  },
  emailButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  emailIcon: {
    fontSize: 18,
  },
  heading: {
    fontSize: 28,
    fontWeight: 600,
    margin: 0,
  },
  metricCell: {
    fontWeight: 500,
    textAlign: 'right',
  },
};

export default TopPerforming; 